import { useEffect, useState } from 'react';
import { Loader2, MapPin, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { usePlaceSearch } from '@/hooks/usePlaceSearch';
import type { PlaceResult } from '@/lib/nominatim';
import type { GeoPoint } from '@/lib/geo';

interface SearchBoxProps {
  onSelect: (p: GeoPoint & { name: string }) => void;
}

export function SearchBox({ onSelect }: SearchBoxProps) {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const { data, isFetching } = usePlaceSearch(query);

  useEffect(() => {
    const id = setTimeout(() => setQuery(input), 400);
    return () => clearTimeout(id);
  }, [input]);

  const pick = (r: PlaceResult) => {
    onSelect({ lat: r.lat, lon: r.lon, name: r.name });
    setInput('');
    setQuery('');
    setOpen(false);
  };

  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setOpen(false);
            (e.target as HTMLInputElement).blur();
          }
          if (e.key === 'Enter' && data && data.length > 0) {
            pick(data[0]);
          }
        }}
        placeholder="Search a place…"
        className="h-9 pl-8 pr-8"
        aria-label="Search places"
      />
      <div className="absolute right-1.5 top-1/2 -translate-y-1/2">
        {isFetching ? (
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        ) : input ? (
          <button
            type="button"
            aria-label="Clear search"
            className="rounded p-0.5 text-muted-foreground hover:text-foreground"
            onMouseDown={(e) => {
              e.preventDefault();
              setInput('');
              setQuery('');
            }}
          >
            <X className="size-4" />
          </button>
        ) : null}
      </div>

      {open && data && data.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full min-w-72 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-lg">
          {data.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-none"
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(r);
                }}
              >
                <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <span className="line-clamp-2">{r.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
